// Five meet the team layouts, from the everyday kit: the cover portrait, the
// studio ID, the name poster, three things about me and the team feature.
// The headline is made from their name ("Meet Jess.", "This is Jess.").
import { flow, textBlock, brand, footer, rule, photo, photoBand, nameTag, display, label, body, strong, widthOf } from '../../kit'
import { dryPainter } from '../../painter'
import { base, ground, serif, detailLine, nameOf, pairs } from './common'

const said = (fact, joiner) => [String(fact.q || '').trim(), String(fact.a || '').trim()].filter(Boolean).join(joiner)

export default {
  // A big photo, like a magazine cover, with their name underneath
  cover(p, env) {
    const { fr, c, k } = base(env)
    const name = nameOf(c)
    photoBand(p, env, {
      key: 'main', photoH: 880, band: k.paper, pad: 34,
      blocks: (f) => [
        f.story ? brand(env, f, k.ink) : null,
        textBlock(name ? `Meet ${name}.` : '', s => ({ ...display(env, k.ink, 120 * s), x: f.left - 4, w: widthOf(f) + 4, maxLines: 2 }), { gap: f.story ? 28 : 0, slot: 'name' }),
        textBlock(c.role, () => ({ ...label(env, k.ink), x: f.left, w: widthOf(f) }), { gap: 20, slot: 'role' }),
        textBlock(c.line, () => ({ ...body(env, k.ink, 34), x: f.left, w: widthOf(f), maxLines: 2 }), { gap: 12, slot: 'line' }),
        footer(env, f, k.ink),
      ],
    })
    if (!fr.story) nameTag(p, env, 44, 44, k.paper, k.ink)
  },

  // An ID card: their photo, their name in capitals, three things about them
  studioId(p, env) {
    const { fr, c, k, w } = base(env)
    const on = ground(p, env, true)
    const facts = pairs(c.facts)
    const pad = 32
    const card = (q, top, s) => {
      const ph = Math.round(470 * s)
      photo(q, env, 'main', { x: fr.left + pad, y: top, w: w - pad * 2, h: ph })
      let y = q.text(nameOf(c), { ...display(env, k.ink, Math.round(104 * s)), upper: true, x: fr.left + pad - 3, y: top + ph + 28, w: w - pad * 2 + 3, maxLines: 1, slot: 'name' }).bottom
      facts.forEach((f, i) => {
        y = q.text(said(f, ': '), { ...body(env, k.ink, 32), x: fr.left + pad, y: y + (i ? 8 : 26), w: w - pad * 2, maxLines: 1, slot: 'facts' }).bottom
      })
      return y
    }
    flow(p, fr, [
      brand(env, fr, on),
      textBlock(c.role, () => ({ ...label(env, on), x: fr.left, w }), { gap: 60, slot: 'role' }),
      {
        gap: 76,
        flex: 1,
        draw: (pp, y, s) => {
          const h = card(pp.isDry ? pp : dryPainter(pp), y + pad, s) - y + pad + 10
          pp.rect(fr.left, y, w, h, k.paper)
          pp.rect(fr.W / 2 - 76, y - 20, 152, 40, k.pop)
          card(pp, y + pad, s)
          return y + h
        },
      },
      footer(env, fr, on),
    ])
  },

  // No photo needed: their initial in a big disc, then their name
  monogram(p, env) {
    const { fr, c, k, w } = base(env)
    const ink = ground(p, env, false)
    const name = nameOf(c)
    const initial = (name.charAt(0) || '?').toUpperCase()
    flow(p, fr, [
      brand(env, fr, ink),
      textBlock(c.role, () => ({ ...label(env, ink), x: fr.left, w }), { gap: 60, slot: 'role' }),
      {
        gap: 50,
        flex: 1,
        draw: (pp, y, s) => {
          const r = Math.round(248 * s)
          const cx = fr.W / 2
          pp.circle(cx, y + r, r, k.accent)
          const size = Math.round(r * 1.4)
          pp.text(initial, { ...serif(env, k.on, size), minSize: size, lh: 1, x: cx - r, y: y + r - Math.round(size * 0.47), w: r * 2, align: 'center', maxLines: 1 })
          return y + r * 2
        },
      },
      textBlock(name, s => ({ ...display(env, ink, 116 * s), x: fr.left - 4, w: w + 4, maxLines: 1 }), { gap: 44, flex: 1, slot: 'name' }),
      textBlock(c.line, s => ({ ...body(env, ink, Math.round(42 * s)), x: fr.left, w, maxLines: 3 }), { gap: 20, slot: 'line' }),
      detailLine(env, fr, c.eyebrow, ink, { slot: 'eyebrow' }),
      footer(env, fr, ink),
    ])
  },

  // A small photo, their name, and three questions answered
  threeThings(p, env) {
    const { fr, c, k, w } = base(env)
    const ink = ground(p, env, false)
    const facts = pairs(c.facts)
    const name = nameOf(c)
    flow(p, fr, [
      brand(env, fr, ink),
      {
        gap: 40,
        draw: (pp, y, s) => {
          const size = Math.round(270 * s)
          const tw = w - size - 36
          photo(pp, env, 'main', { x: fr.right - size, y, w: size, h: size })
          const h = pp.text(name ? `Meet ${name}.` : '', { ...display(env, ink, Math.round(88 * s)), x: fr.left - 3, y: y + 10, w: tw, maxLines: 3, slot: 'name' })
          const r = c.role ? pp.text(c.role, { ...label(env, ink), x: fr.left, y: h.bottom + 26, w: tw, maxLines: 2, slot: 'role' }) : h
          return Math.max(y + size, r.bottom)
        },
      },
      rule(fr, ink, { gap: 40 }),
      facts.length ? {
        gap: 50,
        flex: 1,
        draw: (pp, y, s) => {
          let yy = y
          facts.forEach((f, i) => {
            pp.text(`0${i + 1}`, { ...strong(env, k.accent, 34), x: fr.left, y: yy, w: 80, maxLines: 1 })
            const q = pp.text(String(f.q || ''), { ...strong(env, ink, 32), x: fr.left + 84, y: yy, w: w - 84, maxLines: 1, slot: 'facts' })
            const a = pp.text(String(f.a || ''), { ...display(env, ink, Math.round(54 * s)), x: fr.left + 84, y: q.bottom + 14, w: w - 84, maxLines: 2, slot: 'facts' })
            yy = a.bottom + Math.round(i < facts.length - 1 ? 50 * s : 0)
          })
          return yy
        },
      } : null,
      detailLine(env, fr, c.eyebrow, ink, { slot: 'eyebrow' }),
      footer(env, fr, ink),
    ])
  },

  // Their photo in a tall column beside their name and a few words
  feature(p, env) {
    const { fr, c, w } = base(env)
    const ink = ground(p, env, false)
    const name = nameOf(c)
    const colW = Math.round(w * 0.4)
    const px = fr.left + colW + 40
    flow(p, fr, [
      brand(env, fr, ink),
      {
        gap: 46,
        flex: 1,
        draw: (pp, y, s) => {
          const h = Math.round(940 * s)
          photo(pp, env, 'main', { x: px, y, w: fr.right - px, h })
          const head = pp.text(name ? `This is\n${name}.` : '', { ...display(env, ink, Math.round(100 * s)), x: fr.left - 3, y: y + Math.round(70 * s), w: colW + 3, maxLines: 4, slot: 'name' })
          const role = c.role ? pp.text(c.role, { ...label(env, ink), x: fr.left, y: Math.max(head.bottom + 50, y + Math.round(480 * s)), w: colW, maxLines: 3, slot: 'role' }) : head
          if (c.line) pp.text(c.line, { ...body(env, ink, Math.round(38 * s)), x: fr.left, y: role.bottom + Math.round(56 * s), w: colW, maxLines: 7, maxH: y + h - role.bottom - 56 * s, slot: 'line' })
          return y + h
        },
      },
      detailLine(env, fr, c.eyebrow, ink, { slot: 'eyebrow' }),
      footer(env, fr, ink),
    ])
  },
}
